import * as React from 'react'

import { BookOpen, Question } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

import { ModuleEditor } from './items/module/ModuleEditor'
import { ModuleView } from './items/module/ModuleView'
import { QuizBlockEditor } from './items/quiz/QuizBlockEditor'
import QuizBlockView from './items/quiz/QuizBlockView'
import { ContentEditorProps, ContentViewProps } from './types'

// Type alias for F0 icon components
// The design system doesn't export an IconType, so we infer it from an actual icon
type IconType = typeof BookOpen

export type ContentTypeDefinition<T extends ContentItem = ContentItem> = {
  icon: IconType
  label: () => string
  description: () => string
  editorComponent: React.ComponentType<ContentEditorProps<T>>
  viewComponent: React.ComponentType<ContentViewProps<T>>
}

class ContentTypeRegistry {
  private definitions = new Map<
    string,
    ContentTypeDefinition<ContentItem> & {
      constructor: new (data?: Record<string, unknown>) => ContentItem
      typeString: string
    }
  >()

  register<T extends ContentItem>(
    typeString: string,
    constructor: new (data?: Record<string, unknown>) => T,
    config: ContentTypeDefinition<T>
  ): void {
    const definition = {
      ...config,
      constructor,
      typeString,
    }
    // Type erasure at runtime - all definitions stored with base ContentItem type
    // @ts-expect-error: Generic type T cannot be safely assigned to base type, but this is safe at runtime
    this.definitions.set(typeString, definition)
  }

  get<T extends ContentItem = ContentItem>(
    type: string
  ):
    | (ContentTypeDefinition<T> & {
        constructor: new (data?: Record<string, unknown>) => T
        typeString: string
      })
    | undefined {
    // Caller is responsible for knowing the correct type
    // @ts-expect-error: Widening base type to generic T, safe when caller knows the correct type
    return this.definitions.get(type)
  }

  getAll(): Array<
    ContentTypeDefinition<ContentItem> & {
      constructor: new (data?: Record<string, unknown>) => ContentItem
      typeString: string
    }
  > {
    return Array.from(this.definitions.values())
  }

  createDefaultItem<T extends ContentItem = ContentItem>(type: string): T {
    const definition = this.get<T>(type)
    if (!definition) {
      throw new Error(`No content type registered for type: ${type}`)
    }
    return new definition.constructor()
  }

  getIconForItem(item: ContentItem): IconType | undefined {
    for (const definition of this.definitions.values()) {
      if (item instanceof definition.constructor) {
        return definition.icon
      }
    }
    return undefined
  }

  getLabelForItem(item: ContentItem): string | undefined {
    for (const definition of this.definitions.values()) {
      if (item instanceof definition.constructor) {
        return definition.label()
      }
    }
    return undefined
  }

  getEditorForItem<T extends ContentItem>(
    item: T
  ): React.ComponentType<ContentEditorProps<T>> | undefined {
    const typeString = this.getTypeStringForItem(item)
    if (typeString) {
      const def = this.get<T>(typeString)
      return def?.editorComponent
    }
    return undefined
  }

  getViewForItem<T extends ContentItem>(
    item: T
  ): React.ComponentType<ContentViewProps<T>> | undefined {
    const typeString = this.getTypeStringForItem(item)
    if (typeString) {
      const def = this.get<T>(typeString)
      return def?.viewComponent
    }
    return undefined
  }

  getTypeStringForItem(item: ContentItem): string | undefined {
    for (const [typeString, definition] of this.definitions) {
      if (item instanceof definition.constructor) {
        return typeString
      }
    }
    return undefined
  }
}

export const contentRegistry = new ContentTypeRegistry()

contentRegistry.register('module', ContentModule, {
  icon: BookOpen,
  label: () => i18n.t('trainings.content.content_modal.types.module_label'),
  description: () => i18n.t('trainings.content.content_modal.types.module_description'),
  editorComponent: ModuleEditor,
  viewComponent: ModuleView,
})

contentRegistry.register('quiz', QuizBlock, {
  icon: Question,
  label: () => i18n.t('trainings.content.content_modal.types.quiz_label'),
  description: () => i18n.t('trainings.content.content_modal.types.quiz_description'),
  editorComponent: QuizBlockEditor,
  viewComponent: QuizBlockView,
})
